/*
 * Synced source data — the raw, "unprocessed" records pulled from DHIS2:
 * tracker events, tracked entities, and submitted-aggregate facts.
 *
 * This is the missing UI surface that lets workspace users verify a sync
 * actually produced data, before the tally engine has bucketed it.
 * Counts on TallyMetrics' summary panel point here for the per-record
 * drill. Three streams share one page so the IA matches how operators
 * think about the platform: "show me what landed".
 *
 * Backend endpoints:
 *   GET /api/tracker/events           (filters: programId, orgUnitId)
 *   GET /api/tracker/tracked-entities (filter:  trackedEntityType)
 *   GET /api/aggregate/facts          (filters: dataSetId, periodId, orgUnitId)
 *
 * All three are workspace-scoped server-side via the auth context;
 * platform operators see the full corpus.
 */

import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import TableToolbar from "../components/TableToolbar.js";
import { SkeletonTable } from "../components/Skeleton.js";
import { useTableState } from "../lib/useTableState.js";
import { api, ApiError } from "../lib/api.js";
import { NamedId, useLabels } from "../lib/labels.js";

type Tab = "events" | "teis" | "facts";

type EventRow = Awaited<ReturnType<typeof api.listTrackerEvents>>["events"][number];
type TeiRow = Awaited<ReturnType<typeof api.listTrackedEntities>>["trackedEntities"][number];
type FactRow = Awaited<ReturnType<typeof api.listAggregateFacts>>["facts"][number];

const TAB_LABELS: Record<Tab, string> = {
  events: "Tracker events",
  teis: "Tracked entities",
  facts: "Aggregate facts"
};

function fmtDate(iso: string | undefined): string {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  return new Date(ms).toLocaleString();
}

export default function SyncedSourceData() {
  const [tab, setTab] = useState<Tab>("events");
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [teis, setTeis] = useState<TeiRow[] | null>(null);
  const [facts, setFacts] = useState<FactRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Per-tab server-side filters. Stored separately so switching tabs
  // doesn't lose the filters you set.
  const [programId, setProgramId] = useState("");
  const [orgUnitId, setOrgUnitId] = useState("");
  const [trackedEntityType, setTrackedEntityType] = useState("");
  const [dataSetId, setDataSetId] = useState("");
  const [periodId, setPeriodId] = useState("");
  const [factOrgUnitId, setFactOrgUnitId] = useState("");

  // Drawer for the selected row — full JSON so operators can copy/paste
  // a record into a bug report or sanity-check fields the table omits.
  const [selectedRow, setSelectedRow] = useState<unknown>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      if (tab === "events") {
        const r = await api.listTrackerEvents({
          programId: programId || undefined,
          orgUnitId: orgUnitId || undefined
        });
        setEvents(r.events);
      } else if (tab === "teis") {
        const r = await api.listTrackedEntities({
          trackedEntityType: trackedEntityType || undefined
        });
        setTeis(r.trackedEntities);
      } else {
        const r = await api.listAggregateFacts({
          dataSetId: dataSetId || undefined,
          periodId: periodId || undefined,
          orgUnitId: factOrgUnitId || undefined
        });
        setFacts(r.facts);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // refresh on tab change; filter inputs trigger their own refresh via
    // the Apply button so we don't fire one request per keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Synced source data</h1>
          <p className="muted">
            The raw records pulled from DHIS2 — tracker events, tracked entities, and submitted-aggregate
            facts. Use this to confirm a sync actually produced data before the tally engine buckets it.
          </p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-secondary" onClick={() => void refresh()} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </header>

      <div className="ts-tabs" role="tablist" style={{ marginBottom: 16 }}>
        {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={`ts-tab ${tab === t ? "is-active" : ""}`}
            onClick={() => setTab(t)}
          >
            {TAB_LABELS[t]}
            {t === "events" && events && <span className="muted" style={{ marginLeft: 6 }}>({events.length.toLocaleString()})</span>}
            {t === "teis" && teis && <span className="muted" style={{ marginLeft: 6 }}>({teis.length.toLocaleString()})</span>}
            {t === "facts" && facts && <span className="muted" style={{ marginLeft: 6 }}>({facts.length.toLocaleString()})</span>}
          </button>
        ))}
      </div>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      {tab === "events" && (
        <EventsTab
          rows={events}
          loading={loading}
          programId={programId}
          setProgramId={setProgramId}
          orgUnitId={orgUnitId}
          setOrgUnitId={setOrgUnitId}
          onApply={() => void refresh()}
          onSelectRow={setSelectedRow}
        />
      )}

      {tab === "teis" && (
        <TeisTab
          rows={teis}
          loading={loading}
          trackedEntityType={trackedEntityType}
          setTrackedEntityType={setTrackedEntityType}
          onApply={() => void refresh()}
          onSelectRow={setSelectedRow}
        />
      )}

      {tab === "facts" && (
        <FactsTab
          rows={facts}
          loading={loading}
          dataSetId={dataSetId}
          setDataSetId={setDataSetId}
          periodId={periodId}
          setPeriodId={setPeriodId}
          orgUnitId={factOrgUnitId}
          setOrgUnitId={setFactOrgUnitId}
          onApply={() => void refresh()}
          onSelectRow={setSelectedRow}
        />
      )}

      {selectedRow !== null && (
        <RecordDrawer record={selectedRow} onClose={() => setSelectedRow(null)} />
      )}
    </div>
  );
}

// ---------- per-tab subcomponents ----------

function EventsTab(props: {
  rows: EventRow[] | null;
  loading: boolean;
  programId: string; setProgramId: (s: string) => void;
  orgUnitId: string; setOrgUnitId: (s: string) => void;
  onApply: () => void;
  onSelectRow: (row: EventRow) => void;
}) {
  const labels = useLabels();
  const rowsForTable = useMemo<Array<EventRow & Record<string, unknown>>>(
    () => (props.rows ?? []).map((r) => ({ ...r })),
    [props.rows]
  );
  const t = useTableState(rowsForTable, {
    initialPageSize: 25,
    searchFields: ["id", "programId", "programStageId", "orgUnitId", "trackedEntityId", "enrollmentId", "status"],
    initialSortKey: "lastUpdated",
    initialSortDir: "desc"
  });
  return (
    <>
      <FilterRow>
        <FilterInput label="Program ID" value={props.programId} onChange={props.setProgramId} placeholder="e.g. lxAQ7Zs9VYR" />
        <FilterInput label="Org unit ID" value={props.orgUnitId} onChange={props.setOrgUnitId} placeholder="e.g. ImspTQPwCqd" />
        <button type="button" className="btn btn-primary" onClick={props.onApply}>Apply filters</button>
      </FilterRow>
      {props.rows === null ? <SkeletonTable rows={6} columns={7} /> : props.rows.length === 0 ? (
        <EmptyState what="tracker events" />
      ) : (
        <>
          <TableToolbar
            search={t.search} setSearch={t.setSearch}
            page={t.page} setPage={t.setPage}
            totalPages={t.totalPages} totalRows={t.totalRows}
            pageSize={t.pageSize} setPageSize={t.setPageSize}
            searchPlaceholder="event / program / orgUnit / TEI…"
            exportFilename="tracker-events"
            exportColumns={["id", "programId", "programStageId", "orgUnitId", "eventDate", "status", "trackedEntityId", "lastUpdated"]}
            exportRowsData={t.sorted}
          />
          <table className="transfer-table">
            <thead>
              <tr>
                <th>Event</th><th>Program</th><th>Stage</th><th>Org unit</th>
                <th>Event date</th><th>Status</th><th style={{ textAlign: "right" }}>DV</th><th>Last updated</th>
              </tr>
            </thead>
            <tbody>
              {t.paged.map((r) => (
                <tr key={r.id} className="row-clickable" onClick={() => props.onSelectRow(r)}>
                  <td className="mono" style={{ fontSize: 11 }}>{r.id}</td>
                  <td><NamedId id={r.programId} name={labels.programName(r.programId)} /></td>
                  <td><NamedId id={r.programStageId} name={labels.programStageName(r.programStageId)} /></td>
                  <td><NamedId id={r.orgUnitId} name={labels.orgUnitName(r.orgUnitId)} /></td>
                  <td>{fmtDate(r.eventDate)}</td>
                  <td>{r.status}</td>
                  <td style={{ textAlign: "right" }}>{r.dataValues?.length ?? 0}</td>
                  <td>{fmtDate(r.lastUpdated)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function TeisTab(props: {
  rows: TeiRow[] | null;
  loading: boolean;
  trackedEntityType: string; setTrackedEntityType: (s: string) => void;
  onApply: () => void;
  onSelectRow: (row: TeiRow) => void;
}) {
  const rowsForTable = useMemo<Array<TeiRow & Record<string, unknown>>>(
    () => (props.rows ?? []).map((r) => ({ ...r, attributeCount: Object.keys(r.attributes ?? {}).length })),
    [props.rows]
  );
  const t = useTableState(rowsForTable, {
    initialPageSize: 25,
    searchFields: ["id", "trackedEntityType", "orgUnitId"],
    initialSortKey: "lastUpdated",
    initialSortDir: "desc"
  });
  return (
    <>
      <FilterRow>
        <FilterInput label="Tracked entity type" value={props.trackedEntityType} onChange={props.setTrackedEntityType} placeholder="e.g. nEenWmSyUEp" />
        <button type="button" className="btn btn-primary" onClick={props.onApply}>Apply filters</button>
      </FilterRow>
      {props.rows === null ? <SkeletonTable rows={6} columns={5} /> : props.rows.length === 0 ? (
        <EmptyState what="tracked entities" />
      ) : (
        <>
          <TableToolbar
            search={t.search} setSearch={t.setSearch}
            page={t.page} setPage={t.setPage}
            totalPages={t.totalPages} totalRows={t.totalRows}
            pageSize={t.pageSize} setPageSize={t.setPageSize}
            searchPlaceholder="TEI / type / orgUnit…"
            exportFilename="tracked-entities"
            exportColumns={["id", "trackedEntityType", "orgUnitId", "attributeCount", "createdAt", "lastUpdated"]}
            exportRowsData={t.sorted as unknown as Record<string, unknown>[]}
          />
          <table className="transfer-table">
            <thead>
              <tr>
                <th>TEI</th><th>Type</th><th>Org unit</th>
                <th style={{ textAlign: "right" }}>Attrs</th><th>Created</th><th>Last updated</th>
              </tr>
            </thead>
            <tbody>
              {t.paged.map((r) => (
                <tr key={r.id} className="row-clickable" onClick={() => props.onSelectRow(r)}>
                  <td className="mono" style={{ fontSize: 11 }}>{r.id}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{r.trackedEntityType}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{r.orgUnitId}</td>
                  <td style={{ textAlign: "right" }}>{Object.keys(r.attributes ?? {}).length}</td>
                  <td>{fmtDate(r.createdAt)}</td>
                  <td>{fmtDate(r.lastUpdated)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function FactsTab(props: {
  rows: FactRow[] | null;
  loading: boolean;
  dataSetId: string; setDataSetId: (s: string) => void;
  periodId: string; setPeriodId: (s: string) => void;
  orgUnitId: string; setOrgUnitId: (s: string) => void;
  onApply: () => void;
  onSelectRow: (row: FactRow) => void;
}) {
  const rowsForTable = useMemo<Array<FactRow & Record<string, unknown>>>(
    () => (props.rows ?? []).map((r) => ({ ...r })),
    [props.rows]
  );
  const t = useTableState(rowsForTable, {
    initialPageSize: 25,
    searchFields: ["id", "dataElementId", "dataSetId", "periodId", "orgUnitId", "lineageKey"],
    initialSortKey: "lastUpdated",
    initialSortDir: "desc"
  });
  return (
    <>
      <FilterRow>
        <FilterInput label="Data set ID" value={props.dataSetId} onChange={props.setDataSetId} placeholder="e.g. BfMAe6Itzgt" />
        <FilterInput label="Period ID" value={props.periodId} onChange={props.setPeriodId} placeholder="e.g. 202604" />
        <FilterInput label="Org unit ID" value={props.orgUnitId} onChange={props.setOrgUnitId} placeholder="e.g. ImspTQPwCqd" />
        <button type="button" className="btn btn-primary" onClick={props.onApply}>Apply filters</button>
      </FilterRow>
      {props.rows === null ? <SkeletonTable rows={6} columns={7} /> : props.rows.length === 0 ? (
        <EmptyState what="aggregate facts" />
      ) : (
        <>
          <TableToolbar
            search={t.search} setSearch={t.setSearch}
            page={t.page} setPage={t.setPage}
            totalPages={t.totalPages} totalRows={t.totalRows}
            pageSize={t.pageSize} setPageSize={t.setPageSize}
            searchPlaceholder="fact / data element / period…"
            exportFilename="aggregate-facts"
            exportColumns={["id", "dataElementId", "dataSetId", "periodId", "orgUnitId", "value", "lineageKey", "lastUpdated"]}
            exportRowsData={t.sorted}
          />
          <table className="transfer-table">
            <thead>
              <tr>
                <th>Fact</th><th>Data element</th><th>Data set</th>
                <th>Period</th><th>Org unit</th>
                <th style={{ textAlign: "right" }}>Value</th><th>Last updated</th>
              </tr>
            </thead>
            <tbody>
              {t.paged.map((r) => (
                <tr key={r.id} className="row-clickable" onClick={() => props.onSelectRow(r)}>
                  <td className="mono" style={{ fontSize: 11 }}>{r.id}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{r.dataElementId}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{r.dataSetId ?? "—"}</td>
                  <td>{r.periodId}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{r.orgUnitId}</td>
                  <td style={{ textAlign: "right" }}>{r.value}</td>
                  <td>{fmtDate(r.lastUpdated)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

// ---------- shared helpers ----------

function FilterRow({ children }: { children: React.ReactNode }) {
  return (
    <section
      className="transfer-filters"
      style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}
    >
      {children}
    </section>
  );
}

function FilterInput(props: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="transfer-filter" style={{ minWidth: 180 }}>
      {props.label}
      <input
        type="text"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
      />
    </label>
  );
}

function EmptyState({ what }: { what: string }) {
  return (
    <div className="empty-state-card">
      <h2 style={{ marginTop: 0 }}>No {what} synced yet</h2>
      <p>
        This stream is empty for the current scope. Either no sync has produced records yet, or the
        active filters exclude every record. Try clearing filters; if still empty, run a sync from
        <strong> Administration → DHIS2 instances</strong> and watch <strong>Live monitor</strong> /
        <strong> Sync jobs</strong> for the run to land.
      </p>
    </div>
  );
}

function RecordDrawer({ record, onClose }: { record: unknown; onClose: () => void }) {
  return (
    <aside
      className="card"
      style={{
        position: "fixed", right: 16, top: 80, bottom: 16, width: "min(560px, 90vw)",
        overflow: "auto", boxShadow: "0 12px 40px rgba(0,0,0,0.18)", zIndex: 50
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h2 style={{ margin: 0, fontSize: 14 }}>Record details</h2>
        <button type="button" className="btn btn-sm" onClick={onClose}>Close</button>
      </div>
      <pre
        style={{
          fontSize: 11, lineHeight: 1.4, background: "#0f172a", color: "#e2e8f0",
          padding: 12, borderRadius: 6, overflow: "auto", maxHeight: "calc(100vh - 180px)"
        }}
      >
        {JSON.stringify(record, null, 2)}
      </pre>
    </aside>
  );
}
